/**
 * SafeModeDiarioGiornaliero — versione SEMPLIFICATA del tab Diario.
 * Stessa filosofia di SafeModeLavoroQuotidiano: nessun Radix Select,
 * nessun Card, nessun Dialog, nessun toast, niente animazioni.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/api";

const MONTHS_IT = [
  ["01", "Gennaio"], ["02", "Febbraio"], ["03", "Marzo"], ["04", "Aprile"],
  ["05", "Maggio"], ["06", "Giugno"], ["07", "Luglio"], ["08", "Agosto"],
  ["09", "Settembre"], ["10", "Ottobre"], ["11", "Novembre"], ["12", "Dicembre"],
];

const lastDayOfMonth = (year, monthNum) => {
  const d = new Date(Number(year), Number(monthNum), 0).getDate();
  return String(d).padStart(2, "0");
};

export default function SafeModeDiarioGiornaliero() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterYear, setFilterYear] = useState(() => String(new Date().getFullYear()));
  const [filterMonthFrom, setFilterMonthFrom] = useState("all");
  const [filterMonthTo, setFilterMonthTo] = useState("all");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.getContacts()
      .then((data) => {
        if (!alive) return;
        const list = Array.isArray(data) ? data : (data?.items || []);
        setItems(list);
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.message || "Errore caricamento");
        setLoading(false);
      });
    return () => { alive = false; };
  }, []);

  const yearOptions = useMemo(() => {
    const set = new Set();
    set.add(String(new Date().getFullYear()));
    items.forEach((c) => {
      const d = (c.contactDate || "").slice(0, 10);
      if (d && /^\d{4}-/.test(d)) set.add(d.slice(0, 4));
    });
    return Array.from(set).sort((a, b) => b.localeCompare(a));
  }, [items]);

  const filtered = useMemo(() => {
    if (filterYear === "all") return items;
    const mFrom = filterMonthFrom !== "all" ? filterMonthFrom : null;
    const mToRaw = filterMonthTo !== "all" ? filterMonthTo : null;
    let m1 = mFrom;
    let m2 = mToRaw || mFrom;
    if (m1 && m2 && m1 > m2) { const t = m1; m1 = m2; m2 = t; }
    const from = m1 ? `${filterYear}-${m1}-01` : `${filterYear}-01-01`;
    const to = m2 ? `${filterYear}-${m2}-${lastDayOfMonth(filterYear, m2)}` : `${filterYear}-12-31`;
    return items.filter((c) => {
      const d = (c.contactDate || "").slice(0, 10);
      return d && d >= from && d <= to;
    });
  }, [items, filterYear, filterMonthFrom, filterMonthTo]);

  return (
    <div data-testid="safemode-diario-giornaliero">
      <div style={{ background: "#fef3c7", border: "1px solid #fbbf24", padding: 8, borderRadius: 4, marginBottom: 12, fontSize: 13 }}>
        <strong>MODALITÀ SICURA ATTIVA</strong> — Diario Giornaliero in versione semplificata.
        Disattiva con il pulsante "Esci modalità sicura" in alto.
      </div>
      <div style={{ display: "flex", gap: 12, marginBottom: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
        <label style={{ display: "flex", flexDirection: "column", fontSize: 11, textTransform: "uppercase", color: "#64748b" }}>
          Anno
          <select
            value={filterYear}
            onChange={(e) => setFilterYear(e.target.value)}
            style={{ padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 4, minWidth: 100, marginTop: 4 }}
            data-testid="safemode-diary-year"
          >
            <option value="all">Tutti</option>
            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
        <label style={{ display: "flex", flexDirection: "column", fontSize: 11, textTransform: "uppercase", color: "#64748b" }}>
          Mese da
          <select
            value={filterMonthFrom}
            onChange={(e) => setFilterMonthFrom(e.target.value)}
            disabled={filterYear === "all"}
            style={{ padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 4, minWidth: 130, marginTop: 4 }}
            data-testid="safemode-diary-month-from"
          >
            <option value="all">Tutti i mesi</option>
            {MONTHS_IT.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label style={{ display: "flex", flexDirection: "column", fontSize: 11, textTransform: "uppercase", color: "#64748b" }}>
          Mese a
          <select
            value={filterMonthTo}
            onChange={(e) => setFilterMonthTo(e.target.value)}
            disabled={filterYear === "all" || filterMonthFrom === "all"}
            style={{ padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 4, minWidth: 130, marginTop: 4 }}
            data-testid="safemode-diary-month-to"
          >
            <option value="all">—</option>
            {MONTHS_IT.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
      </div>

      <div style={{ fontSize: 13, color: "#475569", marginBottom: 8 }}>
        {loading ? "Caricamento…" : `${filtered.length} note (di ${items.length} totali)`}
        {error && <span style={{ color: "#dc2626", marginLeft: 8 }}>· Errore: {error}</span>}
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#f1f5f9", textAlign: "left" }}>
              <th style={{ padding: "6px 8px", borderBottom: "1px solid #cbd5e1" }}>Data</th>
              <th style={{ padding: "6px 8px", borderBottom: "1px solid #cbd5e1" }}>Ora</th>
              <th style={{ padding: "6px 8px", borderBottom: "1px solid #cbd5e1" }}>Tipo</th>
              <th style={{ padding: "6px 8px", borderBottom: "1px solid #cbd5e1" }}>Cliente</th>
              <th style={{ padding: "6px 8px", borderBottom: "1px solid #cbd5e1" }}>Descrizione</th>
              <th style={{ padding: "6px 8px", borderBottom: "1px solid #cbd5e1" }}>Operatore</th>
              <th style={{ padding: "6px 8px", borderBottom: "1px solid #cbd5e1" }}>Stato</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: 24, textAlign: "center", color: "#94a3b8" }}>
                {loading ? "Caricamento…" : "Nessuna nota corrisponde ai filtri."}
              </td></tr>
            ) : (
              filtered.slice(0, 200).map((c) => (
                <tr key={c.id} style={{ borderBottom: "1px solid #e2e8f0" }}>
                  <td style={{ padding: "6px 8px" }}>{(c.contactDate || "").slice(0, 10)}</td>
                  <td style={{ padding: "6px 8px" }}>{c.contactTime || "—"}</td>
                  <td style={{ padding: "6px 8px" }}>{c.contactType || "—"}</td>
                  <td style={{ padding: "6px 8px" }}>{c.clientName || "—"}</td>
                  <td style={{ padding: "6px 8px", maxWidth: 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.content || "—"}</td>
                  <td style={{ padding: "6px 8px" }}>{c.operatorName || "—"}</td>
                  <td style={{ padding: "6px 8px" }}>{c.completedAt ? "Concluso" : "In corso"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {filtered.length > 200 && (
          <p style={{ fontSize: 12, color: "#94a3b8", padding: "8px 0", textAlign: "center" }}>
            Mostrate prime 200 righe (di {filtered.length} filtrate). In modalità sicura niente paginazione.
          </p>
        )}
      </div>
    </div>
  );
}
